import styled from "@emotion/styled";
import {css} from "@mui/material";
import {Exist} from "components/Exist";
import {IconClose} from "components/Icons/IconClose";
import {IconLeftArrow} from "components/Icons/IconLeftArrow";
import {defaultTo, isUndefined} from "lodash-es";
import {useDialogModal, useFixedBody} from "modules/utils";
import React, {Fragment, type PropsWithChildren, useCallback, useRef} from "react";
import reactNodeToString from "react-node-to-string";

const BlurBackground = styled.div`
	@keyframes blurAnimation {
		from {
			backdrop-filter: blur(0px);
		}
		to {
			backdrop-filter: blur(7px);
		}
	}
	width: 100%;
	height: 100%;
	position: absolute;
	left: 0;
	top: 0;
	content: "";
	display: block;
	background: rgba(35, 35, 35, 0.5);
	backdrop-filter: blur(0);
	z-index: 10;
	animation: blurAnimation 0.3s ease forwards;
`;

const Modal = styled.dialog<
	Pick<IProps, "animation" | "isDesktop" | "frame" | "isRightSide" | "background">
>`
	position: fixed;
	top: 0;
	left: auto;
	right: auto;
	${(props) => props.frame}: 0;
	margin: 0;
	padding: 0;
	width: 100%;
	height: 100%;
	transform: translateY(100%);
	background: ${({background}) => (background ? background : "#090b0f")};
	background-size: cover;
	display: flex;
	flex-flow: column;
	z-index: 11;
	transition: transform 0.3s ease-in-out;

	${(props) =>
		props.animation !== "right"
			? css`
				&.open {
					transform: translateY(0);
				}

				&.closed {
					opacity: 0;
					transform: translateY(100vh);
					display: none;
				}
			`
			: css`
				&.open {
					transform: translateX(0);
				}

				&.closed {
					transform: translateX(calc(100vw));
					display: none;
				}
			`}

	${(props) =>
		props.isDesktop &&
		css`
			max-width: 400px;
			border-left: 1px solid #575c64;
			max-height: calc(100vh - 115px);
		`}

	${(props) =>
		props.isRightSide &&
		css`
			max-width: calc(100% - 400px);
			border-left: 1px solid #575c64;
		`}
`;

const Header = styled.div`
	//background: #090b0f;
	color: #fff;
	padding: 30px 20px;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	font-style: normal;
	font-weight: bold;
	font-size: 20px;
	line-height: 24px;
	text-align: center;
	font-family: var(--fontFamilyBase);
	min-height: 64px;

	svg {
		width: 20px;
		height: 20px;
	}
`;

const Button = styled.button`
	margin-left: auto;
	position: absolute;
	top: 5px;
	right: 0;
	padding: 20px;
	box-sizing: content-box;
	background: none;
	box-shadow: none;
	border: none;

	svg {
		width: 20px;
		height: 20px;
		pointer-events: none;
		color: #fff;
	}
`;

const BackButton = styled(Button)`
	margin-left: 0;
	position: absolute;
	top: 5px;
	right: auto;
	left: 0;
`;

interface IProps {
	isOpen: boolean;
	withBackButton?: boolean;
	hideCrossButton?: boolean;
	headerText?: React.ReactElement | string;
	onClose?: () => void;
	animation?: "right";
	frame?: "left" | "right" | "full";
	name: string;
	isDesktop?: boolean;
	isRightSide?: boolean;
	isBlur?: boolean;
	background?: string;
	backdropClose?: boolean;
}

export const HOCModal: React.FC<PropsWithChildren<IProps>> = (props) => {
	const isBlur = isUndefined(props.isBlur) ? props.isDesktop : props.isBlur;
	const modalRef = useRef<HTMLDialogElement>(null);

	const onClose = useCallback(() => {
		props.onClose?.();
		modalRef.current?.close();
	}, [props]);

	const handleOutClick = useCallback((event: React.SyntheticEvent) => {
		event.stopPropagation();

		const target = event.target as HTMLElement;

		if (target.tagName === "DIALOG" && props.backdropClose) {
			onClose();
		}
	}, [props.backdropClose, onClose])

	useDialogModal(modalRef, props.isOpen);

	useFixedBody(props.isOpen, props.name);

	const closeButtonLabel = `Close ${reactNodeToString(props.headerText || "")} dialog`;

	return (
		<Fragment>
			<Exist when={!!isBlur && props.isOpen}>
				<BlurBackground onClick={onClose} />
			</Exist>
			<Modal
				className={`${props.isOpen ? "open" : "closed"} safe-area-bottom`}
				animation={props.animation}
				isDesktop={props.isDesktop}
				onClose={(e) => {
					e.stopPropagation();
					onClose();
				}}
				isRightSide={props.isRightSide}
				frame={defaultTo(props.frame, "right")}
				onClick={handleOutClick}
				background={props.background}
				role="dialog"
				aria-modal="true"
				aria-labelledby={`${props.name}-modal`}
				aria-hidden={!props.isOpen}
				aria-live={"polite"}
				ref={modalRef}>
				<Header id={`${props.name}-modal`}>
					<Exist when={!!props.withBackButton}>
						<BackButton onClick={onClose} aria-label={closeButtonLabel} tabIndex={0}>
							<IconLeftArrow />
						</BackButton>
					</Exist>

					<Exist when={!!props.isDesktop && !props.hideCrossButton}>
						<BackButton onClick={onClose} aria-label={closeButtonLabel} tabIndex={0}>
							<IconClose />
						</BackButton>
					</Exist>

					<Exist when={!props.isDesktop && !props.hideCrossButton}>
						<Button onClick={onClose} aria-label={closeButtonLabel} tabIndex={0}>
							<IconClose />
						</Button>
					</Exist>

					<Exist when={!!props.headerText}>{props.headerText}</Exist>
				</Header>

				<Exist when={props.isOpen}>{props.children}</Exist>
			</Modal>
		</Fragment>
	);
};
